// src/router/index.js

import { createRouter, createWebHistory } from "vue-router";

import store from "@/store";
import Cookies from "js-cookie";

import { showToast } from "@/utils/toastService";
import {
  getAuthenticatedLandingRoute,
  isProductionDisplayOnlyUser,
} from "@/modules/identity/domain/productionDisplayAccess.mjs";
import { createPermissionNavigationResolver } from "@/modules/identity/domain/resolvePermissionsBeforeNavigation.mjs";

// Definiujemy stałe dla ścieżek przekierowań
const LOGIN_PATH = "/";
const HOME_PATH = "/home";
const resolvePermissionsBeforeNavigation = createPermissionNavigationResolver(
  () => store.dispatch("fetchPermissions"),
);

const routes = [
  {
    path: "/ekran-produkcji",
    name: "ProductionDisplayBoard",
    component: () =>
      import("@/modules/panels/views/ProductionDisplayBoardView.vue"),
    meta: { requiresAuth: true, fullscreen: true },
  },
  {
    // Home route dostępna dla wszystkich zalogowanych użytkowników
    path: "/home",
    name: "Home",
    component: () => import("../modules/panels/views/HomeView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/zarzadzaniepracownikami",
    name: "ZarzadzaniePracownikami",
    component: () =>
      import("@/modules/identity/views/ZarzadzaniePracownikami.vue"),
    meta: { requiresAuth: true, permissionKey: "pracownicy" },
  },
  {
    path: "/konfiguracja-aplikacji",
    name: "KonfiguracjaAplikacji",
    component: () =>
      import("@/modules/printing/views/ApplicationConfigurationView.vue"),
    meta: { requiresAuth: true, permissionKey: "konfiguracja_aplikacji" },
  },
  {
    path: "/zaladunkilvwchlodnilist",
    name: "ZaladunkiLvWChlodniList",
    component: () =>
      import(
        "@/modules/freeze-drying/views/zaladunki-w-chlodni/ZaladunkiLvWChlodniList.vue"
      ),
    meta: { requiresAuth: true, permissionKey: "zaladunki_chlodnia" },
  },
  {
    path: "/tworzenieliofilizatora",
    name: "TworzenieLiofilizatora",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/TworzenieLiofilizatora.vue"
      ),
    meta: { requiresAuth: true, permissionKey: "liofilizator_add" },
  },
  {
    path: "/kategorieasortymentow",
    name: "KategorieAsortymentow",
    component: () =>
      import("@/modules/definitions/views/KategorieAsortymentow.vue"),
    meta: { requiresAuth: true, permissionKey: "kategorie_asortymentu" },
  },
  {
    path: "/partie-dania/:id",
    name: "SzczegolyPartiiDan",
    component: () =>
      import("@/modules/meal-planning/views/dostawy/SzczegolyPartiiDan.vue"),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: "/historiaZadan",
    name: "HistoriaZadan",
    component: () => import("@/modules/tasks/views/HistoriaZadan.vue"),
    meta: { requiresAuth: true, permissionKey: "rejestr_zadan" },
    props: true,
  },
  {
    path: "/analiza-produkcji",
    name: "AnalizaProdukcji",
    component: () =>
      import("@/modules/analytics/views/ProductionAnalyticsView.vue"),
    meta: { requiresAuth: true, permissionKey: "analiza_produkcji" },
  },
  {
    path: "/analiza-produkcji/liofilizatory/:machineId",
    name: "AnalizaLiofilizatora",
    component: () =>
      import("@/modules/analytics/views/MachineAnalyticsDetailView.vue"),
    meta: { requiresAuth: true, permissionKey: "analiza_produkcji" },
  },
  {
    path: "/panel-brygadzisty",
    component: () => import("@/modules/panels/views/BrygadzistaPanel.vue"),
    meta: { requiresAuth: true },

    children: [
      {
        path: "aktywnetaski",
        name: "BrygadzistaAktywneTaski",
        component: () => import("@/modules/tasks/components/ActiveTaskRaport.vue"),
      },
      {
        path: "mojeraporty",
        name: "BrygadzistaMojeRaporty",
        component: () => import("@/modules/tasks/components/MyTaskRaport.vue"),
      },
      {
        path: "wszystkieraporty",
        name: "BrygadzistaWszystkieRaporty",
        component: () => import("@/modules/tasks/components/AllTaskReports.vue"),
      },
      {
        path: "",
        name: "BrygadzistaDefault", // dodane
        redirect: "/panel-brygadzisty/aktywnetaski",
      },
    ],
  },
  {
    path: "/taskreport",
    name: "TaskReport",
    component: () => import("@/modules/tasks/components/ActiveTaskRaport.vue"),
    meta: { requiresAuth: true, permissionKey: "zadania_aktualne" },
  },
  {
    path: "/zadanie/:taskId/:processType",
    name: "TaskProcess",
    component: () => import("@/modules/tasks/views/TaskProcessView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/arkusz33karty",
    name: "Arkusz33Karty",
    component: () => import("@/modules/sheets/views/Arkusz33Karty.vue"),
    meta: { requiresAuth: true, permissionKey: "formularz_33" },
  },
  {
    path: "/zarzadzaniepracalioflizatora/:id",
    name: "KartaPageDt",
    component: () => import("@/modules/sheets/components/Arkusze/Arkusz33/KartaPageDt.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/magazynforemek",
    name: "MagazynForemek.vue",
    component: () => import("@/modules/warehouses/views/MagazynForemek.vue"),
    meta: { requiresAuth: true, permissionKey: "magazyn_foremki" },
  },
  {
    path: "/magazynopakowan",
    name: "MagazynOpakowan.vue",
    component: () => import("@/modules/warehouses/views/MagazynOpakowan.vue"),
    meta: { requiresAuth: true, permissionKey: "magazyn_opakowania" },
  },
  {
    path: "/magazynsuchyplf",
    name: "MagazynSuchyPlf",
    component: () => import("@/modules/warehouses/views/MagazynSuchyPlf.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/dostawypartiidania",
    name: "DostawyPartiiDania.vue",
    component: () =>
      import("@/modules/meal-planning/views/dostawy/DostawyPartiiDania.vue"),
    meta: { requiresAuth: true, permissionKey: "dostawy_dan" },
  },

  {
    path: "/zarzadzaniepracalioflizatora",
    name: "ZarzadzaniePracaLioflizatora",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/ZarzadzaniePracaLioflizatora.vue"
      ),
    props: (route) => ({ machine: route.query.machine || "" }),
    meta: { requiresAuth: true, permissionKey: "zarzadzanie_praca_lio" },
  },
  {
    path: "/planowanie",
    name: "PlanowanieCyklu",
    component: () =>
      import("@/modules/meal-planning/views/planowanie/PlanowanieCyklu.vue"),
    props: (route) => ({ machine: route.query.machine || "" }),
    meta: { requiresAuth: true, permissionKey: "planowanie_cykli" },
  },
  {
    path: "/planowanieprzydzialdan",
    name: "PlanowaniePrzydzialDan",
    component: () =>
      import(
        "@/modules/meal-planning/views/planowanie/PlanowaniePrzydzialuDan.vue"
      ),
    props: (route) => ({ machine: route.query.machine || "" }),
    meta: { requiresAuth: true, permissionKey: "planowanie_cykli_dania" },
  },

  {
    path: "/zarzadzaniedostawcami",
    name: "ZarzadzanieDostawcami",
    component: () =>
      import("@/modules/suppliers/views/ZarzadzanieDostawcami.vue"),
    props: (route) => ({ machine: route.query.machine || "" }),
    meta: { requiresAuth: true, permissionKey: "dostawcy" },
  },

  {
    path: "/wprowadzaniepartiidania",
    name: "WprowadzaniePartiiDania",
    component: () =>
      import(
        "@/modules/meal-planning/views/planowanie/WprowadzaniePartiiDania.vue"
      ),
    props: (route) => ({ machine: route.query.machine || "" }),
    meta: { requiresAuth: true, permissionKey: "planowanie_dostawy_dan" },
  },

  {
    path: "/podgladparametrow",
    name: "podgladParametrow",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/podgladParametrow.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/grupowaedcyjaparametrow",
    name: "GrupowaEdcyjaParametrow",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/GrupowaEdcyjaParametrow.vue"
      ),
    meta: { requiresAuth: true },
  },

  {
    path: "/parametrymaszynyadd",
    name: "BrakParametrowAdd",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/BrakParametrowAdd.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/asortymenparametry",
    name: "AsortymenParametry",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/AsortymenParametry.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/zarzadzajparametrami",
    name: "Parametry",
    component: () =>
      import("@/modules/freeze-drying/views/liofilizatory/SetParametryLio.vue"),
    meta: { requiresAuth: true, permissionKey: "parametry" },
  },

  {
    path: "/zarzadzanieasortymentem",
    name: "ZarzadzanieAsortymentem",
    component: () =>
      import(
        "@/modules/freeze-drying/views/liofilizatory/ZarzadzanieAsortymentem.vue"
      ),
    meta: { requiresAuth: true, permissionKey: "asortyment" },
  },
  {
    path: "/liofilizatory",
    name: "Liofilizatory",
    component: () =>
      import("@/modules/freeze-drying/views/liofilizatory/Liofilizatory.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/zarzadzaniePanel",
    name: "PanelZarzadzania",
    component: () => import("@/modules/panels/views/PanelZarzadzania.vue"),
    meta: { requiresAuth: true, userType: ["admin"] }, // tylko admin
  },
  {
    path: "/panel-uzytkownika",
    name: "PanelUzytkownika",
    component: () => import("@/modules/panels/views/PanelUzytkownika.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/tworzenie-paneli",
    name: "TworzeniePaneli",
    component: () => import("@/modules/panels/views/TworzeniePaneli.vue"),

    meta: {
      requiresAuth: true,
      userType: ["admin"],
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: () => import("@/modules/identity/views/Profile.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: LOGIN_PATH,
    name: "Login",
    component: () => import("@/modules/identity/views/Login.vue"),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});
function hasRequiredUserType(userTypeRequired) {
  if (!userTypeRequired) return true; // Jeśli nie ma wymaganego typu, dostęp jest otwarty

  const currentUserType = store.getters.userType; // Pobierz userType z Vuex

  if (Array.isArray(userTypeRequired)) {
    return userTypeRequired.includes(currentUserType);
  }

  return currentUserType === userTypeRequired;
}

async function verifyAuthentication(to, next) {
  try {
    // 1) Uwierzytelnienie
    if (!store.getters.isAuthenticated) {
      await store.dispatch("fetchUser");
    }

    if (isProductionDisplayOnlyUser(store.getters.userType)) {
      if (to.name !== "ProductionDisplayBoard") {
        return next({ name: "ProductionDisplayBoard" });
      }

      return next();
    }

    // 2) Dociągnij uprawnienia (raz na sesję)
    await resolvePermissionsBeforeNavigation(store.getters.permissionsLoaded);

    // 3) (opcjonalnie) wciąż możesz zostawić userType, jeśli chcesz mieć filtr ról
    const userTypeRequired = to.meta.userType;
    if (userTypeRequired) {
      const ok = hasRequiredUserType(userTypeRequired);
      if (!ok) {
        showToast({
          severity: "warn",
          summary: "Brak dostępu",
          detail: "Ta strona wymaga innego typu konta.",
        });
        return next(HOME_PATH);
      }
    }

    // 4) Sprawdź permissionKey (dynamiczne uprawnienia z backendu)
    const permissionKey = to.meta.permissionKey;
    if (permissionKey && !store.getters.hasPermission(permissionKey)) {
      showToast({
        severity: "warn",
        summary: "Brak uprawnień",
        detail: "Nie masz dostępu do tej funkcji.",
      });
      return next(HOME_PATH);
    }

    return next();
  } catch (_error) {
    showToast({
      severity: "error",
      summary: "Sesja wygasła",
      detail: "Zaloguj się ponownie.",
    });
    store.dispatch("clearUser");
    return next(LOGIN_PATH);
  }
}

// Globalny strażnik tras
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  const token = Cookies.get("auth_token");

  if (to.name === "Login" && token) {
    return next(getAuthenticatedLandingRoute(store.getters.userType));
  }

  if (requiresAuth) {
    if (!token) {
      store.dispatch("clearUser");
      return next(LOGIN_PATH);
    }

    // ✅ Teraz przekazujemy cały obiekt `to` (a nie tylko userTypeRequired)
    return verifyAuthentication(to, next);
  }

  return next();
});

export default router;
